/**
 * The four-pillar model, as a real catalogue filter.
 *
 * Blackout · Light Filtering · Motorized · Obscura Pro. A visitor arrives with a
 * PROBLEM ("I need it dark"), picks the pillar in the nav, and lands on the
 * products listing already filtered to that pillar — free to narrow further by
 * type (Honeycomb, Roller, Zebra …) using the existing category filter.
 *
 * Membership is an explicit slug list, not a category or a name match, because
 * neither is reliable here:
 *   • Category over-includes. The Blackout landing page pulled whole categories
 *     and so listed "Light Filtering Roller Shades" and "Designer Roller Shades".
 *   • Category is also wrong in places. "Cordless Honeycomb (26mm)" sits in the
 *     Motorized Blinds category but is cordless, so it is NOT in `motorized`.
 *
 * There is no blackout/opacity flag on `products`; adding one is the proper
 * long-term fix, and then this list becomes a query. Until then, a product added
 * to the catalogue will not appear under a pillar until it is added here.
 *
 * NOTE: `/products?opacity=blackout` links already existed in page copy but the
 * listing never read that param — they silently returned the unfiltered grid.
 * The param implemented here is `style`.
 */

export type PillarKey = 'blackout' | 'light-filtering' | 'motorized';

export interface Pillar {
  key: PillarKey;
  label: string;
  /** One line under the listing heading. */
  blurb: string;
  productSlugs: string[];
}

export const PILLARS: Record<PillarKey, Pillar> = {
  blackout: {
    key: 'blackout',
    label: 'Blackout',
    blurb: 'Block the light for sleep, nurseries and media rooms.',
    productSlugs: [
      'blackout-roller-shades-side-channels',
      'cordless-blackout-pleated-shades',
      'motorized-blackout-cellular-shades',
    ],
  },
  'light-filtering': {
    key: 'light-filtering',
    label: 'Light Filtering',
    blurb: 'Soften the sun and keep the view, with daytime privacy.',
    productSlugs: [
      'light-filtering-roller-shades',
      'single-cell-light-filtering-shades',
      'cordless-sheer-shades',
      'exterior-solar-shades',
      '1-percent-openness-solar-shades',
      '5-percent-openness-solar-shades',
    ],
  },
  motorized: {
    key: 'motorized',
    label: 'Motorized',
    blurb: 'Remote, app and voice control, with schedules.',
    productSlugs: [
      'motorized-roller-shades',
      'motorized-solar-shades',
      'motorized-blackout-cellular-shades',
      'motorized-faux-wood-blinds',
    ],
  },
};

export function isPillarKey(v: string | null | undefined): v is PillarKey {
  return !!v && Object.prototype.hasOwnProperty.call(PILLARS, v);
}

export function getPillar(v: string | null | undefined): Pillar | null {
  return isPillarKey(v) ? PILLARS[v] : null;
}

/** `/products?style=blackout` */
export function pillarHref(key: PillarKey): string {
  return `/products?style=${key}`;
}


/* ────────────────────────────────────────────────────────────────────────────
 * DB-DRIVEN MATCHING (2026-09-21)
 *
 * The slug lists above were a stopgap: they lived in code, so marking products
 * in the database changed nothing on the site. These predicates read the real
 * columns instead:
 *   • Motorized      → products.control_types, exact item in the comma list.
 *                      Every active product now carries 'Motorized', because
 *                      motorisation is an option on 34 categories — so this
 *                      pillar intentionally returns the whole catalogue.
 *   • Blackout       → products.tags contains 'blackout' (or 'room darkening')
 *   • Light Filtering→ products.tags contains 'light filtering' / 'sheer' /
 *                      'solar shade'
 * Add a tag in the admin and the pillar picks it up; no deploy needed.
 * ──────────────────────────────────────────────────────────────────────────── */

export interface PillarMatchable {
  slug?: string;
  control_types?: string | null;
  tags?: string | null;
}

/** Exact membership of a comma-separated list, trimmed and case-insensitive. */
function listHas(list: string | null | undefined, value: string): boolean {
  if (!list) return false;
  return list.split(',').some((v) => v.trim().toLowerCase() === value.toLowerCase());
}

function tagsInclude(tags: string | null | undefined, needles: string[]): boolean {
  if (!tags) return false;
  const parts = tags.split(',').map((t) => t.trim().toLowerCase());
  return needles.some((n) => parts.some((t) => t.includes(n)));
}

export function matchesPillar(key: PillarKey, p: PillarMatchable): boolean {
  switch (key) {
    case 'motorized':
      return listHas(p.control_types, 'Motorized');
    case 'blackout':
      return tagsInclude(p.tags, ['blackout', 'room darkening']);
    case 'light-filtering':
      return tagsInclude(p.tags, ['light filtering', 'sheer', 'solar shade']);
    default:
      return false;
  }
}
